import { useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface ShortcutConfig {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean; // Command key on Mac
  shiftKey?: boolean;
  action: () => void;
  description: string;
}

/**
 * Custom hook for handling keyboard shortcuts
 *
 * @param enabled - Whether shortcuts are enabled (useful for disabling in forms)
 * @returns Object with shortcut information
 */
export const useKeyboardShortcuts = (enabled: boolean = true) => {
  const navigate = useNavigate();

  // Define shortcuts
  const shortcuts: ShortcutConfig[] = useMemo(
    () => [
      {
        key: "u",
        metaKey: true,
        action: () => navigate("/users"),
        description: "Go to Users",
      },
      {
        key: "p",
        metaKey: true,
        action: () => navigate("/profile"),
        description: "Go to Profile",
      },
      {
        key: "h",
        metaKey: true,
        action: () => navigate("/"),
        description: "Go to Home/Dashboard",
      },
      {
        key: "/",
        metaKey: true,
        action: () => {
          // Focus search input if available
          const searchInput = document.querySelector(
            'input[type="search"], input[placeholder*="Search"]',
          ) as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.select();
          }
        },
        description: "Focus search",
      },
    ],
    [navigate],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input/textarea
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      // Allow Cmd+/ to work even in inputs (for search focus)
      const isSearchShortcut = event.metaKey && event.key === "/";

      if (!enabled || (isTyping && !isSearchShortcut)) {
        return;
      }

      // Check each shortcut
      for (const shortcut of shortcuts) {
        const isCtrlMatch = shortcut.ctrlKey ? event.ctrlKey : true;
        const isMetaMatch = shortcut.metaKey ? event.metaKey : true;
        const isShiftMatch = shortcut.shiftKey ? event.shiftKey : true;
        const isKeyMatch =
          event.key.toLowerCase() === shortcut.key.toLowerCase();

        if (isKeyMatch && isCtrlMatch && isMetaMatch && isShiftMatch) {
          event.preventDefault();
          shortcut.action();
          break;
        }
      }
    },
    [enabled, shortcuts],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Show shortcuts help with ?
  const handleHelpShortcut = useCallback(
    (event: KeyboardEvent) => {
      if (
        event.key === "?" &&
        event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey
      ) {
        const target = event.target as HTMLElement;
        const isTyping =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable;

        if (!isTyping) {
          event.preventDefault();
          // You could show a modal here with all shortcuts
          console.log(
            "Keyboard shortcuts:",
            shortcuts.map(
              (s) => `${s.description}: Cmd+${s.key.toUpperCase()}`,
            ),
          );
        }
      }
    },
    [shortcuts],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleHelpShortcut);

    return () => {
      window.removeEventListener("keydown", handleHelpShortcut);
    };
  }, [handleHelpShortcut]);

  return {
    shortcuts,
    isEnabled: enabled,
  };
};

/**
 * Hook for page-specific keyboard shortcuts
 * Useful for shortcuts that only work on specific pages
 */
export const usePageShortcuts = (shortcuts: ShortcutConfig[]) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isTyping) {
        return;
      }

      for (const shortcut of shortcuts) {
        const isCtrlMatch =
          shortcut.ctrlKey === undefined || shortcut.ctrlKey === event.ctrlKey;
        const isMetaMatch =
          shortcut.metaKey === undefined || shortcut.metaKey === event.metaKey;
        const isShiftMatch =
          shortcut.shiftKey === undefined ||
          shortcut.shiftKey === event.shiftKey;
        const isKeyMatch =
          event.key.toLowerCase() === shortcut.key.toLowerCase();

        if (isKeyMatch && isCtrlMatch && isMetaMatch && isShiftMatch) {
          event.preventDefault();
          shortcut.action();
          break;
        }
      }
    },
    [shortcuts],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
};
